import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";
import { Cents } from "./money";
import { VatBasis } from "./price";
import { ProductKind } from "./product";

/**
 * The shop's catalog (spec, "API": "catalogus (lijst, zoeken, product met varianten,
 * beschikbaarheid en de prijs die voor de klant geldt)"). Public: a guest or a signed-in customer.
 *
 * Only what is for sale appears: active products with at least one active variant, and only those
 * variants. Inactive and deleted products and variants are never in an answer.
 *
 * Prices are the ones for whoever asks: a guest pays Consument prices incl VAT; with a customer
 * session (`__Host-customer-session`) the customer's group and own prices apply, and a business
 * group's prices are excl VAT. `vatBasis` says which side of VAT `amount` is on; `amountExcl` and
 * `amountIncl` give both. Answers that depend on a session are sent `Cache-Control: private`.
 */

// ---------------------------------------------------------------------------------------------
// Fields

/**
 * A product photo as the shop shows it: the original and the sizes made from it (smallest first,
 * at least 400, 800 and 1600 pixels wide). URLs may be root-relative.
 */
export const ShopPhoto = z.object({
	id: z.string(),
	/** The photo's `alt`: its alt text as written in the admin, or the product's name without one. */
	alt: z.string(),
	url: z.string(),
	sizes: z.array(z.object({ width: z.number().int(), url: z.string() })),
});
export type ShopPhoto = z.infer<typeof ShopPhoto>;

/** What one unit costs the buyer. */
export const ShopPrice = z.object({
	/** The side of VAT `amount` is on: Consument incl VAT, business groups excl VAT. */
	vatBasis: VatBasis,
	/** The unit price to show, on `vatBasis`. */
	amount: Cents,
	amountExcl: Cents,
	amountIncl: Cents,
	/** The product's VAT rate, an integer percentage. */
	vatRate: z.number().int(),
});
export type ShopPrice = z.infer<typeof ShopPrice>;

/** A product's price in a list: the lowest of its variants, and whether the others differ ("vanaf"). */
export const ShopFromPrice = ShopPrice.extend({
	/** True when the product's variants do not all cost the same: show "vanaf". */
	from: z.boolean(),
});
export type ShopFromPrice = z.infer<typeof ShopFromPrice>;

/**
 * `pricePerPiece` in a list or on a product page (#161): the lowest price per piece over the
 * variants that have pieces, on the same side of VAT as `price`, and whether they differ ("vanaf").
 */
export const ShopFromPricePerPiece = z.object({
	/** In cents, rounded half up: "€0,35 per doek". */
	amount: Cents,
	/** What a piece is called for the variant with the lowest price per piece ("doek"); `null`: not said. */
	pieceNoun: z.string().nullable(),
	/** True when the variants with pieces do not all cost the same per piece: show "vanaf". */
	from: z.boolean(),
});
export type ShopFromPricePerPiece = z.infer<typeof ShopFromPricePerPiece>;

/** "From `minQuantity` pieces, `percent` off": the unit price at that step, for this buyer. */
export const ShopQuantityPrice = z.object({
	minQuantity: z.number().int(),
	percent: z.number().int(),
	price: ShopPrice,
});
export type ShopQuantityPrice = z.infer<typeof ShopQuantityPrice>;

/**
 * A group (`category`): a division of the assortment with its own page in the shop
 * (`/producten/{slug}`). Groups come in `position` order, lowest first.
 */
export const ShopCategory = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	position: z.number().int(),
	/** The short text on the group's page; can be empty. */
	description: z.string(),
	/** For the page's `<title>`: the group's SEO title, or its name when that is empty. */
	seoTitle: z.string(),
	/** For the meta description: the group's SEO description, or else the start of its text; can be empty. */
	seoDescription: z.string(),
});
export type ShopCategory = z.infer<typeof ShopCategory>;

/** A fit (`fit`): the variant "past op" (`fits`) the holder, or is "niet voor" it (`notFor`). */
export const ShopFit = z.enum(["fits", "notFor"]);
export type ShopFit = z.infer<typeof ShopFit>;

// ---------------------------------------------------------------------------------------------
// The list

/** A product in the shop's list. */
export const ShopProductSummary = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	kind: ProductKind,
	/** The first photo, or `null` without photos. */
	photo: ShopPhoto.nullable(),
	price: ShopFromPrice,
	/** The lowest price per piece; `null` when no variant has pieces. */
	pricePerPiece: ShopFromPricePerPiece.nullable(),
	/** At least one variant can be ordered now. */
	inStock: z.boolean(),
	/**
	 * The size label (`sizeLabel`) to show with the product: that of the variant whose price `price`
	 * shows (the cheapest), so the two belong together. `null` when that variant has no size or fit.
	 */
	sizeLabel: z.string().nullable(),
});
export type ShopProductSummary = z.infer<typeof ShopProductSummary>;

/**
 * Ids in a query string: repeated (`?value=a&value=b`), in bracket notation (`?value[0]=a`, as the
 * oRPC client sends an array) or comma-separated (`?value=a,b`). A bad value is no filter.
 */
const IdList = z
	.union([z.string(), z.array(z.string())])
	.transform((ids) =>
		(Array.isArray(ids) ? ids : ids.split(","))
			.map((id) => id.trim())
			.filter((id) => id !== "")
			.slice(0, 50),
	)
	.optional()
	.catch(undefined);

/**
 * The shop's product list: searched on name, description, SKU and EAN; filtered on kind, on being
 * in stock, on a group, on attribute values and on "past op"; sorted by name, by price (the buyer's
 * lowest) or by when it was added (`sort=createdAt&order=desc`: newest first).
 *
 * Attribute values combine as the shop's filters do: values of one attribute are alternatives
 * (46×27 cm or 40×40 cm), different attributes all apply (46×27 cm and wasbaar). Several holders
 * in `fits` are alternatives too. Values, "past op" and `inStock` must all hold for one and the
 * same variant for sale (`ShopVariant.attributeValueIds`: a value set on the product holds for its
 * variants). Value and holder ids the catalog does not know are ignored, as any bad value in a URL;
 * a group slug it does not know holds nothing.
 */
export const ShopProductListInput = listInput({
	sort: ["name", "price", "createdAt"],
	defaultSort: "name",
	filters: {
		kind: ProductKind.optional().catch(undefined),
		/** `yes`: only products with at least one variant in stock. */
		inStock: z.enum(["yes"]).optional().catch(undefined),
		/** A group's slug: only the products in that group. */
		category: z.string().trim().min(1).max(200).optional().catch(undefined),
		/** Attribute value ids (`ShopProductListPage.attributes`). */
		value: IdList,
		/** Holder ids: only products with a variant that "past op" one of them (`ShopProductListPage.fits`). */
		fits: IdList,
	},
});
export type ShopProductListInput = z.input<typeof ShopProductListInput>;
/** The list query with its defaults filled in, as the API receives it. */
export type ShopProductListQuery = z.output<typeof ShopProductListInput>;

/**
 * The filters with their counts: how many products the list would hold with that one choice
 * changed, every other filter (search, kind, in stock, the other attributes) kept. For a value of
 * an attribute, the attribute's own selection is left out: the count is what choosing that value
 * gives, alone or next to the values already chosen.
 */
export const ShopCategoryCount = ShopCategory.extend({ count: z.number().int() });
export type ShopCategoryCount = z.infer<typeof ShopCategoryCount>;

/** An attribute with every value, in their set order, each with its count (zero included). */
export const ShopAttributeCount = z.object({
	id: z.string(),
	name: z.string(),
	/** The size attribute (formaat): its values are the sizes the size label shows. At most one is. */
	isSize: z.boolean(),
	values: z.array(z.object({ id: z.string(), name: z.string(), count: z.number().int() })),
});
export type ShopAttributeCount = z.infer<typeof ShopAttributeCount>;

/** A holder for the "past op" filter: how many products have a variant that fits it. */
export const ShopHolderCount = z.object({
	id: z.string(),
	name: z.string(),
	/** One of them, for "je XXL-wisser" (#195); `null`: not filled in, say it with `name`. */
	singularName: z.string().nullable(),
	count: z.number().int(),
	/**
	 * The sizes of the variants for sale that "past op" it (`46×27 cm`), each once, in the size
	 * attribute's set order; empty when none has a size (#212). Among the products the search and
	 * kind leave, whatever the other filters (group, values, "past op", in stock) choose.
	 */
	sizes: z.array(z.string()),
});
export type ShopHolderCount = z.infer<typeof ShopHolderCount>;

export const ShopProductListPage = listPage(ShopProductSummary).extend({
	/** Every group in `position` order, with its count. */
	categories: z.array(ShopCategoryCount),
	/** Every attribute in `position` order, with its values' counts. */
	attributes: z.array(ShopAttributeCount),
	/** Every holder in `position` order, with its "past op" count. */
	fits: z.array(ShopHolderCount),
});
export type ShopProductListPage = z.infer<typeof ShopProductListPage>;

// ---------------------------------------------------------------------------------------------
// The detail

/** An option with the values the product's active variants hold, in their set order. */
export const ShopOption = z.object({
	id: z.string(),
	name: z.string(),
	values: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ShopOption = z.infer<typeof ShopOption>;

/** An attribute with the values its variants for sale hold (`ShopVariant.attributeValueIds`), in set order. */
export const ShopAttribute = z.object({
	id: z.string(),
	name: z.string(),
	values: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ShopAttribute = z.infer<typeof ShopAttribute>;

/** A stated fit of a variant on a holder. A holder that is not listed is unknown, never "past op". */
export const ShopVariantFit = z.object({
	holderId: z.string(),
	/** As the size label says it: "bekende XXL-wissers". */
	name: z.string(),
	/** One of them, for "Dit past niet op je XXL-wisser" (#195); `null`: not filled in. */
	singularName: z.string().nullable(),
	fit: ShopFit,
});
export type ShopVariantFit = z.infer<typeof ShopVariantFit>;

/** A variant for sale. */
export const ShopVariant = z.object({
	id: z.string(),
	/** The value names in option order (`XXL / Blauw`); the product's name without options. */
	name: z.string(),
	sku: z.string(),
	ean: z.string().nullable(),
	/** One value per option of the product. */
	optionValues: z.array(z.object({ optionId: z.string(), optionValueId: z.string() })),
	/** The price of one piece. */
	price: ShopPrice,
	/**
	 * How many pieces (doekjes, navullingen) one pack holds; `null` for a product without pieces. A
	 * bundle without its own number has its components' summed (quantity × pieces).
	 */
	piecesPerPack: z.number().int().nullable(),
	/**
	 * What one piece is called, singular ("doek", "navulling"); `null`: not said. A bundle without its
	 * own takes its components' when they all agree.
	 */
	pieceNoun: z.string().nullable(),
	/**
	 * `price.amount` divided by `piecesPerPack`, in cents rounded half up; `null` without pieces. Not
	 * an order line's unit price, which is per pack.
	 */
	pricePerPiece: Cents.nullable(),
	/** The quantity discount steps that apply to this buyer, ascending; empty without any. */
	quantityPrices: z.array(ShopQuantityPrice),
	/**
	 * Whether it can be ordered now. The shop shows yes or no, never a count; the cart and checkout
	 * check the exact number themselves.
	 */
	inStock: z.boolean(),
	/**
	 * The attribute values that hold for it, in attribute and value order: its product's, except
	 * that where it holds values of its own for an attribute, those replace the product's.
	 */
	attributeValueIds: z.array(z.string()),
	/** Its size: its value of the size attribute (formaat), from `attributeValueIds`; `null` without one. */
	size: z.string().nullable(),
	/** Its stated fits, in holder order: "past op" and "niet voor". */
	fits: z.array(ShopVariantFit),
	/** "46×27 cm · past op bekende XXL-wissers": the size with the fits; `null` with neither. */
	sizeLabel: z.string().nullable(),
});
export type ShopVariant = z.infer<typeof ShopVariant>;

/**
 * One variant in a bundle, for "wat zit erin" (#161): its name and how many one bundle holds, never
 * its price.
 */
export const ShopBundleComponent = z.object({
	variantId: z.string(),
	/** The product's name, with the variant's value names after a dash: "Vloerwisser – XXL". */
	name: z.string(),
	/** Per bundle. */
	quantity: z.number().int(),
	/** The component's own pieces per pack; `null` without pieces. */
	piecesPerPack: z.number().int().nullable(),
	pieceNoun: z.string().nullable(),
});
export type ShopBundleComponent = z.infer<typeof ShopBundleComponent>;

/** One product as the shop's product page shows it. */
export const ShopProduct = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	kind: ProductKind,
	/** Plain text as Richard typed it. */
	description: z.string(),
	/** For the page's `<title>`: the product's SEO title, or its name when that is empty. */
	seoTitle: z.string(),
	/**
	 * For the page's meta description: the product's SEO description, or else the start of its
	 * description (at most `SEO_DESCRIPTION_FALLBACK_LENGTH` characters, cut at a word); can be empty.
	 */
	seoDescription: z.string(),
	photos: z.array(ShopPhoto),
	options: z.array(ShopOption),
	/** By the first option's value order, then the second's, and so on (as the admin's grid). */
	variants: z.array(ShopVariant),
	price: ShopFromPrice,
	/** The lowest price per piece; `null` when no variant has pieces. */
	pricePerPiece: ShopFromPricePerPiece.nullable(),
	/** A bundle's components in their set order; empty for a standard product. */
	components: z.array(ShopBundleComponent),
	/** At least one variant can be ordered now. */
	inStock: z.boolean(),
	/** The groups the product is in, in `position` order. */
	categories: z.array(ShopCategory),
	/** The attributes with the values its variants hold, in `position` order. */
	attributes: z.array(ShopAttribute),
	/** The size label under the photos: as `ShopProductSummary.sizeLabel`. Each variant has its own. */
	sizeLabel: z.string().nullable(),
});
export type ShopProduct = z.infer<typeof ShopProduct>;

/** The longest description fallback for `ShopProduct.seoDescription`: what search engines show. */
export const SEO_DESCRIPTION_FALLBACK_LENGTH = 160;

const SlugPath = z.object({ slug: z.string().min(1) });

// ---------------------------------------------------------------------------------------------
// Procedures (public: a guest, or a signed-in customer for their own prices)

export const ShopCatalogContract = {
	listProducts: oc
		.route({ method: "GET", path: "/shop/catalog/products" })
		.input(ShopProductListInput)
		.output(ShopProductListPage),
	/** Errors: `CatalogProductNotFound` (404): no such slug, or the product is not for sale. */
	getProduct: oc
		.route({ method: "GET", path: "/shop/catalog/products/{slug}" })
		.input(SlugPath)
		.output(ShopProduct),
};
